import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import type { MembershipRole } from '@starter/shared';
import { useAuth } from '../auth/AuthContext.js';
import { tenantRole } from '../auth/routing.js';
import { HealthBadge } from './HealthBadge.js';
import { ProgramContextSelect } from './ProgramContextSelect.js';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

const NAV_BY_ROLE: Record<MembershipRole, NavItem[]> = {
  STUDENT: [
    { to: '/student', label: 'Dashboard', end: true },
    { to: '/student/sheets', label: 'Clinical Sheets' },
    { to: '/student/cases', label: 'Cases' },
    { to: '/student/progress', label: 'Progress' },
  ],
  INSTRUCTOR: [
    { to: '/instructor', label: 'Dashboard', end: true },
    { to: '/instructor/review', label: 'Review Queue' },
    { to: '/instructor/students', label: 'Students' },
    { to: '/instructor/cases', label: 'Cases' },
  ],
  PROGRAM_DIRECTOR: [
    { to: '/pd', label: 'Dashboard', end: true },
    { to: '/pd/cohorts', label: 'Cohorts' },
    { to: '/pd/students', label: 'Students' },
    { to: '/pd/instructors', label: 'Instructors' },
    { to: '/pd/sites', label: 'Clinical Sites' },
    { to: '/pd/specialties', label: 'Specialties' },
    { to: '/pd/procedures', label: 'Procedures' },
    { to: '/pd/requirements', label: 'Requirements' },
    { to: '/pd/settings', label: 'Settings' },
  ],
};

const ROLE_LABELS: Record<MembershipRole, string> = {
  STUDENT: 'Student',
  INSTRUCTOR: 'Instructor',
  PROGRAM_DIRECTOR: 'Program Director',
};

export function AppLayout() {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (!user) return null;

  const role = tenantRole(user);
  if (!role) return null;

  const navItems = NAV_BY_ROLE[role];

  return (
    <div className="layout">
      <button
        type="button"
        className="layout__menu-toggle"
        aria-label="Toggle navigation"
        onClick={() => setSidebarOpen((v) => !v)}
      >
        <span />
        <span />
        <span />
      </button>

      {sidebarOpen && (
        <div
          className="layout__backdrop"
          onClick={() => setSidebarOpen(false)}
          role="presentation"
        />
      )}

      <aside className={`layout__sidebar ${sidebarOpen ? 'layout__sidebar--open' : ''}`}>
        <div className="layout__brand">
          <div className="layout__logo">SL</div>
          <div>
            <strong>ScrubLedger</strong>
            <span className="layout__tagline">Less paperwork. More teaching.</span>
          </div>
        </div>

        <nav className="layout__nav" aria-label="Main">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `layout__nav-link ${isActive ? 'layout__nav-link--active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="layout__main">
        <header className="layout__header">
          <div className="layout__header-info">
            <span className="layout__user-name">{user.fullName}</span>
            <span className="layout__user-role">{ROLE_LABELS[role]}</span>
            {user.authKind === 'tenant' && user.programName && (
              <span className="layout__user-program">{user.programName}</span>
            )}
          </div>
          <div className="layout__header-actions">
            <ProgramContextSelect />
            <HealthBadge />
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => void logout()}>
              Sign out
            </button>
          </div>
        </header>

        <main className="layout__content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
